/**
 * =============================================================================
 * USE CHAT FAQ HOOK
 * =============================================================================
 * 
 * React hook for FAQ search and suggestions in customer support chat
 * Provides auto-suggestions when agent types responses
 * 
 * @fileoverview FAQ search hook for chat suggestions
 * @author FinMatrix Development Team
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/axios';
import { useDebounce } from '@/hooks/useDebounce';

export interface FaqItem {
  id: number;
  category: string;
  question: string;
  answer: string;
  keywords?: string | null;
}

interface UseChatFaqOptions {
  enabled?: boolean;
  debounceMs?: number;
}

interface UseChatFaqReturn {
  faqs: FaqItem[];
  categories: Array<{ category: string; count: number }>;
  isLoading: boolean;
  error: string | null;
  searchFaqs: (query: string, category?: string) => Promise<void>;
  getCategories: () => Promise<void>;
}

export const useChatFaq = (options: UseChatFaqOptions = {}): UseChatFaqReturn => {
  const { enabled = true } = options;
  
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [categories, setCategories] = useState<Array<{ category: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchFaqs = useCallback(async (query: string, category?: string) => {
    if (!enabled) return;

    // Google-style: Keep previous results visible while loading (don't clear immediately)
    setIsLoading(true);
    setError(null);
    
    // Don't clear faqs here - keep previous results visible during loading

    try {
      const params: Record<string, string> = { limit: '10' };
      if (query.trim()) {
        params.q = query.trim();
      }
      if (category) {
        params.category = category;
      }

      const res = await API.get('/api/v1/chat-faq/search', { params });
      
      if (res.data.success) {
        const data = res.data.data;
        
        // Handle grouped data (when no query) or flat array (when searching)
        if (Array.isArray(data)) {
          // Always update with new search results, even if empty
          setFaqs(data);
        } else if (data && typeof data === 'object') {
          // Grouped by category - flatten it
          const flatFaqs: FaqItem[] = [];
          Object.values(data).forEach((categoryFaqs) => {
            if (Array.isArray(categoryFaqs)) {
              flatFaqs.push(...(categoryFaqs as FaqItem[]));
            }
          });
          // Always update with new search results, even if empty
          setFaqs(flatFaqs);
        } else {
          // Clear if no data returned (empty search result)
          setFaqs([]);
        }
      } else {
        // Clear on unsuccessful response
        setFaqs([]);
      }
    } catch (err: unknown) {
      console.error('Failed to search FAQs:', err);
      let message: string | null = null;
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        message = axiosError.response?.data?.message || null;
      }
      setError(message || 'Failed to search FAQs');
      // Clear on error to show fresh results on next search
      setFaqs([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  const getCategories = useCallback(async () => {
    if (!enabled) return;

    try {
      const res = await API.get('/api/v1/chat-faq/categories');
      if (res.data.success) {
        setCategories(res.data.data || []);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch FAQ categories:', err);
    }
  }, [enabled]);

  // Load categories on mount
  useEffect(() => {
    if (enabled) {
      getCategories();
    }
  }, [enabled, getCategories]);

  return {
    faqs,
    categories,
    isLoading,
    error,
    searchFaqs,
    getCategories,
  };
};

/**
 * Hook for debounced FAQ search (auto-suggestions)
 */
export const useChatFaqSuggestions = (
  query: string,
  options: UseChatFaqOptions = {}
) => {
  const { debounceMs = 200, enabled = true } = options;
  const debouncedQuery = useDebounce(query, debounceMs);
  const { faqs, isLoading, searchFaqs, error } = useChatFaq({ enabled });

  useEffect(() => {
    if (enabled) {
      if (debouncedQuery.trim().length >= 1) {
        // Google-style: search as soon as user types 1 character
        // Always search when query changes, regardless of current FAQ count
        searchFaqs(debouncedQuery);
      } else if (debouncedQuery.trim().length === 0 && faqs.length === 0) {
        // Only search for popular FAQs if we have no results yet
        // Don't clear existing results when query becomes empty
        searchFaqs('');
      }
      // If query is empty but we have faqs, keep them visible (don't search again)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, enabled, searchFaqs]); // faqs.length intentionally excluded to ensure search runs on every query change

  return {
    suggestions: faqs,
    isLoading,
    error,
  };
};

